import { useEffect, useRef, useState } from 'react';
import { retryQuotationGeneration } from '../../lib/quotationApi';
import { fetchRfqDetail, type RfqDetailDto } from '../../lib/rfqApi';

// D99.13: the ONE owner of "Retry generation" state. RfqInboxPage unmounts RfqDetail on every
// selection change, so state kept there dies with the instance; it lives here, keyed by RFQ id,
// and RfqDetail only displays the view for the RFQ it is showing.
type Ge = RfqDetailDto['generationError'];
type Entry = {
  retrying?: boolean;
  // Why the last retry failed (the server's code; absent when the request never landed).
  failure?: { code?: string } | null;
  // The generationError and status the RFQ STORED, re-read after a failure. `was` = what the screen had when
  // the retry began; a newer load of the RFQ differs from it, so the newer load wins.
  stored?: { was: string; ge: Ge; status?: string };
};
export type RfqRetryView = { retrying: boolean; failure: { code?: string } | null; override: { ge: Ge; status?: string } | null };

const geKey = (ge?: { code?: string; at?: string } | null) => `${ge?.code}|${ge?.at}`;

export function useRfqRetry(opts: {
  currentIdRef: React.MutableRefObject<string | undefined>; // the page's current selection
  onSucceeded: (id: string, quotationId: string) => void;
}) {
  const [byId, setById] = useState<Record<string, Entry>>({});
  const mounted = useRef(true);
  const inFlight = useRef(new Set<string>());
  const optsRef = useRef(opts);
  optsRef.current = opts;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const patch = (id: string, p: Partial<Entry>) => setById((m) => ({ ...m, [id]: { ...m[id], ...p } }));

  const start = async (rfq: { _id?: string; generationError?: { code?: string; at?: string } | null }) => {
    const id = rfq?._id;
    if (!id || inFlight.current.has(id)) return;
    inFlight.current.add(id);
    patch(id, { retrying: true, failure: null });
    try {
      const quotationId = await retryQuotationGeneration(id);
      // Only if that RFQ is still the selected one; otherwise its normal load shows the result.
      if (mounted.current && optsRef.current.currentIdRef.current === id) optsRef.current.onSucceeded(id, quotationId);
    } catch (err) {
      patch(id, { failure: { code: (err as { code?: string } | null)?.code } });
      const was = geKey(rfq.generationError);
      try {
        const fresh = await fetchRfqDetail(id);
        patch(id, { stored: { was, ge: fresh.generationError ?? null, status: fresh.status } });
      } catch {
        // Re-read failed: the banner keeps what it showed; the "Retry failed" line already tells the user.
      }
    } finally {
      inFlight.current.delete(id);
      patch(id, { retrying: false });
    }
  };

  const view = (id: string | undefined, loadedGe?: { code?: string; at?: string } | null): RfqRetryView => {
    const e = (id && byId[id]) || {};
    return {
      retrying: !!e.retrying,
      failure: e.failure ?? null,
      override: e.stored && e.stored.was === geKey(loadedGe) ? { ge: e.stored.ge, status: e.stored.status } : null,
    };
  };

  return { start, view };
}
